import React, { useState, useEffect, useCallback } from 'react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import styled from 'styled-components';
import { useToast } from '../Context/ToastContext';

/**
 * StaffingPositionsPage — a labour staffing agency's own requisition board.
 *
 * Openings turn over weekly, so authoring them as seed files meant every new requisition
 * needed an engineer. This writes to the same opportunity maps the brain reads, so a position
 * opened here is live in the widget on the next conversation.
 *
 * The vocabulary is deliberately the recruiter's, not the engine's:
 *   Must have  → a requirement    (needed to be put forward)
 *   Rules out  → a blocker        (disqualifies, or has to be worked around)
 *   Bonus      → an unlock factor (opens more roles; worth asking if cheap)
 * Nobody outside this codebase has heard of an unlock factor. Everybody knows what a bonus is.
 */

const KINDS = [
    { value: 'must', label: 'Must have', hint: 'They need this to be put forward' },
    { value: 'rules_out', label: 'Rules out', hint: 'This disqualifies them, or has to be worked around' },
    { value: 'bonus', label: 'Bonus', hint: 'Opens more roles — worth asking' },
];

const blankQuestion = () => ({ kind: 'must', question: '', answers: [] });

export default function StaffingPositionsPage() {
    const axios = useAuthenticatedAxios();
    const toast = useToast();

    const [employers, setEmployers] = useState([]);
    const [tradeFamilies, setTradeFamilies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState('');
    const [editing, setEditing] = useState(null);      // { domain, position }
    const [employerForm, setEmployerForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [board, families] = await Promise.all([
                axios.get('/staffing/board'),
                axios.get('/staffing/trade-families'),
            ]);
            setEmployers(board.data?.employers || []);
            setTradeFamilies(families.data?.tradeFamilies || []);
            setDenied('');
        } catch (e) {
            const msg = e?.response?.data?.message || 'Could not load the board';
            if (e?.response?.status === 403) setDenied(msg); else toast?.error?.(msg);
        } finally { setLoading(false); }
    }, [axios, toast]);

    useEffect(() => { load(); }, [load]);

    const togglePosition = async (domain, position) => {
        const next = !position.isOpen;
        // Optimistic: this is the weekly operation and it should feel instant.
        setEmployers(prev => prev.map(e => e.domain !== domain ? e : {
            ...e, positions: e.positions.map(p => p.id === position.id ? { ...p, isOpen: next } : p),
        }));
        try {
            await axios.patch(`/staffing/employers/${domain}/positions/${position.id}/status`, { isOpen: next });
        } catch (e) {
            toast?.error?.(e?.response?.data?.message || 'Could not change the status');
            load();
        }
    };

    const savePosition = async () => {
        const { domain, position } = editing;
        if (!position.displayName?.trim()) { toast?.error?.('Give the position a name'); return; }
        if (!position.tradeFamily) { toast?.error?.('Pick a trade family'); return; }
        setSaving(true);
        try {
            await axios.put(
                `/staffing/employers/${domain}/positions/${position.id || 'new'}`,
                {
                    displayName: position.displayName,
                    description: position.description,
                    tradeFamily: position.tradeFamily,
                    isOpen: position.isOpen !== false,
                    screening: (position.screening || []).filter(q => q.question?.trim()),
                },
            );
            toast?.success?.('Position saved');
            setEditing(null);
            load();
        } catch (e) {
            toast?.error?.(e?.response?.data?.message || 'Could not save');
        } finally { setSaving(false); }
    };

    const saveEmployer = async () => {
        if (!employerForm.displayName?.trim()) { toast?.error?.('Give the employer a name'); return; }
        setSaving(true);
        try {
            await axios.put(`/staffing/employers/${employerForm.domain || employerForm.displayName}`, {
                displayName: employerForm.displayName,
                employerQuestions: (employerForm.employerQuestions || []).filter(q => q?.trim()),
            });
            toast?.success?.('Employer saved');
            setEmployerForm(null);
            load();
        } catch (e) {
            toast?.error?.(e?.response?.data?.message || 'Could not save');
        } finally { setSaving(false); }
    };

    const removePosition = async (domain, position) => {
        if (!window.confirm(`Delete "${position.displayName}" permanently? Closing it keeps the screening for when it reopens.`)) return;
        try {
            await axios.delete(`/staffing/employers/${domain}/positions/${position.id}`);
            toast?.success?.('Position deleted');
            load();
        } catch (e) { toast?.error?.(e?.response?.data?.message || 'Could not delete'); }
    };

    const removeEmployer = async (domain, name, count) => {
        if (!window.confirm(`Delete ${name} and its ${count} position(s)? This cannot be undone.`)) return;
        try {
            await axios.delete(`/staffing/employers/${domain}`);
            toast?.success?.('Employer deleted');
            load();
        } catch (e) { toast?.error?.(e?.response?.data?.message || 'Could not delete'); }
    };

    const familyLabel = slug => tradeFamilies.find(f => f.slug === slug)?.label || slug;
    const openCount = employers.reduce((n, e) => n + e.positions.filter(p => p.isOpen).length, 0);
    const totalCount = employers.reduce((n, e) => n + e.positions.length, 0);

    if (denied) {
        return <Page><Wrap><H1>Positions</H1><Card><Muted>{denied}</Muted></Card></Wrap></Page>;
    }

    return (
        <Page>
            <Wrap>
                <H1>Positions</H1>
                <Lead>
                    Your client employers and the roles you are placing into. The widget screens every
                    candidate against whatever is <b>open</b> here — a position you open is live on the
                    next conversation, and one you close stops being offered immediately.
                </Lead>

                {loading ? <Card><Muted>Loading…</Muted></Card> : (
                    <>
                        <Row style={{ marginBottom: 16 }}>
                            <PrimaryBtn onClick={() => setEmployerForm({ displayName: '', employerQuestions: [''] })}>
                                Add employer
                            </PrimaryBtn>
                            <Muted style={{ marginTop: 0 }}>
                                {openCount} open of {totalCount} across {employers.length} employer(s)
                            </Muted>
                        </Row>

                        {employers.length === 0 && (
                            <Card><Muted>No employers yet. Add your first client to start listing positions.</Muted></Card>
                        )}

                        {employers.map(emp => (
                            <Card key={emp.domain}>
                                <Row style={{ justifyContent: 'space-between' }}>
                                    <div>
                                        <EmployerName>{emp.displayName}</EmployerName>
                                        <Muted style={{ marginTop: 2 }}>
                                            {emp.positions.filter(p => p.isOpen).length} open · {emp.positions.length} total
                                            {emp.employerQuestions.length > 0 && ` · ${emp.employerQuestions.length} shared question(s)`}
                                        </Muted>
                                    </div>
                                    <Row>
                                        <GhostBtn onClick={() => setEmployerForm({
                                            domain: emp.domain,
                                            displayName: emp.displayName,
                                            employerQuestions: emp.employerQuestions.length ? emp.employerQuestions : [''],
                                        })}>Edit employer</GhostBtn>
                                        <GhostBtn onClick={() => setEditing({
                                            domain: emp.domain,
                                            position: { displayName: '', description: '', tradeFamily: '', isOpen: true, screening: [blankQuestion()] },
                                        })}>Add position</GhostBtn>
                                        <DangerBtn onClick={() => removeEmployer(emp.domain, emp.displayName, emp.positions.length)}>Delete</DangerBtn>
                                    </Row>
                                </Row>

                                {emp.employerQuestions.length > 0 && (
                                    <MiniCard style={{ marginTop: 12 }}>
                                        <Muted style={{ marginTop: 0, marginBottom: 4 }}>
                                            Asked once for this employer, before any position question:
                                        </Muted>
                                        {emp.employerQuestions.map((q, i) => <div key={i}>· {q}</div>)}
                                    </MiniCard>
                                )}

                                <div style={{ marginTop: 12 }}>
                                    {emp.positions.length === 0 && <Muted>No positions yet.</Muted>}
                                    {emp.positions.map(pos => (
                                        <PositionRow key={pos.id} $open={pos.isOpen}>
                                            <div style={{ flex: 1, minWidth: 220 }}>
                                                <Row style={{ gap: 8 }}>
                                                    <strong>{pos.displayName}</strong>
                                                    <Tag>{familyLabel(pos.tradeFamily)}</Tag>
                                                    <StatusPill $open={pos.isOpen}>{pos.isOpen ? 'Open' : 'Closed'}</StatusPill>
                                                </Row>
                                                <Muted style={{ marginTop: 4 }}>
                                                    {['must', 'rules_out', 'bonus'].map(k => {
                                                        const n = (pos.screening || []).filter(q => q.kind === k).length;
                                                        return n ? `${n} ${KINDS.find(x => x.value === k).label.toLowerCase()}` : null;
                                                    }).filter(Boolean).join(' · ') || 'No screening questions yet'}
                                                </Muted>
                                            </div>
                                            <Row>
                                                <GhostBtn onClick={() => togglePosition(emp.domain, pos)}>
                                                    {pos.isOpen ? 'Close' : 'Reopen'}
                                                </GhostBtn>
                                                <GhostBtn onClick={() => setEditing({
                                                    domain: emp.domain,
                                                    position: { ...pos, screening: (pos.screening || []).map(q => ({ ...q })) },
                                                })}>Edit</GhostBtn>
                                                <DangerBtn onClick={() => removePosition(emp.domain, pos)}>Delete</DangerBtn>
                                            </Row>
                                        </PositionRow>
                                    ))}
                                </div>
                            </Card>
                        ))}
                    </>
                )}

                {/* ── employer editor ─────────────────────────────────────── */}
                {employerForm && (
                    <Overlay onClick={e => e.target === e.currentTarget && setEmployerForm(null)}>
                        <Modal>
                            <H2>{employerForm.domain ? 'Edit employer' : 'Add employer'}</H2>
                            <Field style={{ marginBottom: 14 }}>
                                <Label>Employer name</Label>
                                <TextInput
                                    value={employerForm.displayName}
                                    placeholder="e.g. Freshouse — Meat Processing"
                                    onChange={e => setEmployerForm({ ...employerForm, displayName: e.target.value })}
                                />
                            </Field>
                            <Label>Questions asked once for this employer</Label>
                            <Muted style={{ marginTop: 0, marginBottom: 8 }}>
                                Things true of every role here — asked once, not repeated per position.
                            </Muted>
                            {(employerForm.employerQuestions || []).map((q, i) => (
                                <Row key={i} style={{ marginBottom: 8 }}>
                                    <TextInput
                                        value={q}
                                        placeholder="e.g. Have you worked in metal fabrication before?"
                                        onChange={e => {
                                            const next = [...employerForm.employerQuestions];
                                            next[i] = e.target.value;
                                            setEmployerForm({ ...employerForm, employerQuestions: next });
                                        }}
                                    />
                                    <GhostBtn onClick={() => setEmployerForm({
                                        ...employerForm,
                                        employerQuestions: employerForm.employerQuestions.filter((_, j) => j !== i),
                                    })}>Remove</GhostBtn>
                                </Row>
                            ))}
                            <GhostBtn onClick={() => setEmployerForm({
                                ...employerForm, employerQuestions: [...(employerForm.employerQuestions || []), ''],
                            })}>Add question</GhostBtn>

                            <Row style={{ marginTop: 20, justifyContent: 'flex-end' }}>
                                <GhostBtn onClick={() => setEmployerForm(null)}>Cancel</GhostBtn>
                                <PrimaryBtn disabled={saving} onClick={saveEmployer}>{saving ? 'Saving…' : 'Save'}</PrimaryBtn>
                            </Row>
                        </Modal>
                    </Overlay>
                )}

                {/* ── position editor ─────────────────────────────────────── */}
                {editing && (
                    <Overlay onClick={e => e.target === e.currentTarget && setEditing(null)}>
                        <Modal>
                            <H2>{editing.position.id ? 'Edit position' : 'Add position'}</H2>
                            <Grid style={{ marginBottom: 14 }}>
                                <Field>
                                    <Label>Position name</Label>
                                    <TextInput
                                        value={editing.position.displayName}
                                        placeholder="e.g. Welder"
                                        onChange={e => setEditing({ ...editing, position: { ...editing.position, displayName: e.target.value } })}
                                    />
                                </Field>
                                <Field>
                                    <Label>Trade family</Label>
                                    <Select
                                        value={editing.position.tradeFamily}
                                        onChange={e => setEditing({ ...editing, position: { ...editing.position, tradeFamily: e.target.value } })}
                                    >
                                        <option value="">Select…</option>
                                        {tradeFamilies.map(f => <option key={f.slug} value={f.slug}>{f.label}</option>)}
                                    </Select>
                                </Field>
                            </Grid>
                            <Field style={{ marginBottom: 14 }}>
                                <Label>What the role involves</Label>
                                <TextInput
                                    value={editing.position.description || ''}
                                    placeholder="One line — what the person actually does"
                                    onChange={e => setEditing({ ...editing, position: { ...editing.position, description: e.target.value } })}
                                />
                            </Field>

                            <Label>Screening questions</Label>
                            <Muted style={{ marginTop: 0, marginBottom: 10 }}>
                                What the widget asks a candidate for this role. Put the answers that count in
                                the second box — for a “rules out” question, the answers that mean it applies.
                            </Muted>

                            {(editing.position.screening || []).map((q, i) => (
                                <QuestionCard key={i}>
                                    <Row style={{ marginBottom: 8 }}>
                                        <Select
                                            style={{ maxWidth: 150 }}
                                            value={q.kind}
                                            onChange={e => {
                                                const next = [...editing.position.screening];
                                                next[i] = { ...q, kind: e.target.value };
                                                setEditing({ ...editing, position: { ...editing.position, screening: next } });
                                            }}
                                        >
                                            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                                        </Select>
                                        <Muted style={{ marginTop: 0, flex: 1 }}>{KINDS.find(k => k.value === q.kind)?.hint}</Muted>
                                        <GhostBtn onClick={() => setEditing({
                                            ...editing,
                                            position: { ...editing.position, screening: editing.position.screening.filter((_, j) => j !== i) },
                                        })}>Remove</GhostBtn>
                                    </Row>
                                    <TextInput
                                        style={{ marginBottom: 8 }}
                                        value={q.question}
                                        placeholder="e.g. Can you set up and operate MIG and TIG welding equipment?"
                                        onChange={e => {
                                            const next = [...editing.position.screening];
                                            next[i] = { ...q, question: e.target.value };
                                            setEditing({ ...editing, position: { ...editing.position, screening: next } });
                                        }}
                                    />
                                    <TextInput
                                        value={(q.answers || []).join(', ')}
                                        placeholder="Answers that count, comma separated — e.g. yes, mig, tig, i can"
                                        onChange={e => {
                                            const next = [...editing.position.screening];
                                            next[i] = { ...q, answers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) };
                                            setEditing({ ...editing, position: { ...editing.position, screening: next } });
                                        }}
                                    />
                                </QuestionCard>
                            ))}

                            <GhostBtn onClick={() => setEditing({
                                ...editing,
                                position: { ...editing.position, screening: [...(editing.position.screening || []), blankQuestion()] },
                            })}>Add question</GhostBtn>

                            <Row style={{ marginTop: 20, justifyContent: 'flex-end' }}>
                                <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
                                <PrimaryBtn disabled={saving} onClick={savePosition}>{saving ? 'Saving…' : 'Save position'}</PrimaryBtn>
                            </Row>
                        </Modal>
                    </Overlay>
                )}
            </Wrap>
        </Page>
    );
}

const Page = styled.div`display:flex;justify-content:center;width:100%;height:100%;background:linear-gradient(135deg,#0a0d12,#111820 50%,#0d1117);padding:32px;box-sizing:border-box;overflow-y:auto;`;
const Wrap = styled.div`width:100%;max-width:1100px;color:#fff;padding-bottom:60px;`;
const H1 = styled.h1`font-size:24px;margin:0 0 6px;`;
const H2 = styled.h2`font-size:18px;margin:0 0 16px;`;
const Lead = styled.p`color:#9fb0c0;line-height:1.5;margin:0 0 18px;max-width:760px;`;
const Card = styled.div`background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:14px;padding:18px;margin-bottom:16px;`;
const MiniCard = styled.div`background:rgba(0,0,0,0.25);border-radius:10px;padding:10px 12px;font-size:13px;line-height:1.6;color:#cdd9e5;`;
const QuestionCard = styled.div`background:rgba(0,0,0,0.22);border:1px solid rgba(255,255,255,0.07);border-radius:10px;padding:12px;margin-bottom:10px;`;
const Grid = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:14px;`;
const Field = styled.div`display:flex;flex-direction:column;`;
const Label = styled.div`font-size:12.5px;color:#8aa0b4;margin:0 0 6px;`;
const inputBase = `width:100%;box-sizing:border-box;background:rgba(0,0,0,0.35);border:1px solid rgba(255,255,255,0.12);border-radius:8px;color:#fff;padding:9px 11px;font-size:14px;outline:none;&:focus{border-color:#4f8cff;}`;
const Select = styled.select`${inputBase} color-scheme: dark; & option { background:#0d1117; color:#e6edf3; }`;
const TextInput = styled.input`${inputBase}`;
const Row = styled.div`display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;
const Muted = styled.div`color:#7d8fa1;font-size:12.5px;margin-top:8px;`;
const EmployerName = styled.div`font-size:16px;font-weight:600;`;
const PrimaryBtn = styled.button`background:linear-gradient(135deg,#4f8cff,#2f6fe0);color:#fff;border:none;border-radius:8px;padding:10px 18px;font-size:14px;font-weight:600;cursor:pointer;&:disabled{opacity:.5;cursor:not-allowed;}`;
const GhostBtn = styled.button`background:rgba(255,255,255,0.07);color:#dbe6f0;border:1px solid rgba(255,255,255,0.12);border-radius:8px;padding:8px 14px;font-size:13px;cursor:pointer;&:hover{background:rgba(255,255,255,0.12);}`;
const DangerBtn = styled(GhostBtn)`color:#f8a3a3;&:hover{background:rgba(153,27,27,0.25);}`;
const Tag = styled.span`background:rgba(255,255,255,0.1);border-radius:20px;padding:1px 8px;font-size:11px;`;
const StatusPill = styled.span`font-size:11px;font-weight:600;border-radius:20px;padding:2px 10px;background:${p => p.$open ? '#16653433' : '#44444433'};color:${p => p.$open ? '#4ade80' : '#94a3b8'};`;
const PositionRow = styled.div`display:flex;gap:12px;align-items:center;justify-content:space-between;flex-wrap:wrap;padding:12px;border:1px solid rgba(255,255,255,0.07);border-radius:10px;margin-bottom:8px;opacity:${p => p.$open ? 1 : 0.6};`;
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.6);display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;overflow-y:auto;z-index:1000;`;
const Modal = styled.div`background:#0d1117;border:1px solid rgba(255,255,255,0.12);border-radius:14px;padding:24px;width:100%;max-width:720px;color:#fff;`;
